import { useState, useEffect, useRef } from "react";
import api from "../api/axios";
import Card from "../components/UI/Card";
import Input from "../components/UI/Input";
import Button from "../components/UI/Button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const OTP_TTL = 1 * 60; // 1 minute in seconds

function getSecondsLeft() {
  const sentAt = Number(localStorage.getItem("otp_sent_at") || 0);
  if (!sentAt) return OTP_TTL;
  const elapsed = Math.floor((Date.now() - sentAt) / 1000);
  return Math.max(0, OTP_TTL - elapsed);
}

export default function VerifyOtp() {
  const nav = useNavigate();
  const { fetchMe } = useAuth();
  const email = localStorage.getItem("otp_email") || "";
  const [otp, setOtp] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(getSecondsLeft);
  const timerRef = useRef(null);

  function startTimer() {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft(getSecondsLeft());
    }, 1000);
  }

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (secondsLeft === 0) clearInterval(timerRef.current);
  }, [secondsLeft]);

  const mins = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const secs = String(secondsLeft % 60).padStart(2, "0");
  const expired = secondsLeft === 0;
  const urgent = secondsLeft > 0 && secondsLeft <= 30;

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      localStorage.setItem("token", data.token);
      localStorage.removeItem("otp_sent_at");
      sessionStorage.removeItem("otp_password");
      await fetchMe();
      nav("/");
    } catch (err) {
      setMsg(err?.response?.data?.message || "OTP verify failed");
    } finally {
      setLoading(false);
    }
  }

  async function resendOtp() {
    const password = sessionStorage.getItem("otp_password");
    if (!password) { nav("/login"); return; }
    setResending(true);
    setMsg("");
    setOtp("");
    try {
      await api.post("/auth/login", { email, password });
      localStorage.setItem("otp_sent_at", Date.now().toString());
      setSecondsLeft(OTP_TTL);
      startTimer();
      setMsg("✅ New OTP sent to your email.");
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to resend OTP. Please login again.");
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="h-full flex items-center justify-center p-4" style={{ background: "rgb(var(--bg))" }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl text-white text-2xl font-bold mb-4 shadow-lg"
            style={{ background: "rgb(var(--primary))" }}
          >R</div>
          <div className="text-2xl font-bold tracking-tight">Check your email</div>
          <div className="text-sm opacity-40 mt-1">OTP sent to <span className="font-semibold opacity-70">{email}</span></div>
        </div>

        <Card className="p-7">
          {/* Countdown timer */}
          <div className="flex items-center justify-center gap-2 mb-5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"
              style={{ color: expired ? "#ef4444" : urgent ? "#f97316" : "rgb(var(--primary))", opacity: 0.8 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {expired ? (
              <span className="text-sm font-semibold" style={{ color: "#ef4444" }}>OTP expired</span>
            ) : (
              <span className="text-sm font-semibold" style={{ color: urgent ? "#f97316" : "rgb(var(--primary))" }}>
                OTP valid for <span className="font-mono">{mins}:{secs}</span>
              </span>
            )}
          </div>

          {msg && (
            <div className={`mb-4 px-3 py-2.5 rounded-xl text-sm border ${msg.startsWith("✅") ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
              {msg}
            </div>
          )}

          {expired ? (
            <div className="space-y-3 text-center">
              <p className="text-sm opacity-50">Your OTP has expired. Request a new one.</p>
              <Button loading={resending} className="w-full" onClick={resendOtp}>
                {resending ? "Sending..." : "Resend OTP"}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider opacity-50 mb-1.5">One-Time Password</label>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className="text-center text-xl tracking-widest font-bold"
                />
              </div>
              <Button loading={loading} className="w-full mt-2">
                {loading ? "Verifying..." : "Verify & Login"}
              </Button>
            </form>
          )}
        </Card>

        <div className="text-center text-xs opacity-30 mt-6">Ramaa Group &copy; {new Date().getFullYear()}</div>
      </div>
    </div>
  );
}
